import { CheckIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import {
  SkuActionsMenu,
  SkuMenuOption,
} from '@views/SKUMaster/components/SkuActionsMenu';
import {
  Supplier,
  SupplierReadiness,
  SupplierSkuLinkView,
  certBadgeIntentOf,
  certLabelOf,
  sourceTypeBadgeIntentOf,
  sourceTypeLabelOf,
  statusBadgeIntentOf,
  statusLabelOf,
} from './supplierTypes';

export interface SupplierTableRowProps {
  supplier: Supplier;
  menuOptions: SkuMenuOption[];
  isCopied: boolean;
  onCopy: (supplier: Supplier) => void;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
}

const MAX_BRAND_CHIPS = 3;

const pluralise = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const leadTimeDisplay = (link: SupplierSkuLinkView): string => {
  if (link.leadTimeMeanDays === null) return '—';
  return link.leadTimeStdDays !== null
    ? `${link.leadTimeMeanDays} ± ${link.leadTimeStdDays} d`
    : `${link.leadTimeMeanDays} d`;
};

const orderMultipleDisplay = (link: SupplierSkuLinkView): string => {
  if (link.orderMultiple === null) return '—';
  return `${link.orderMultiple} ${link.orderMultipleUnit ?? ''}`.trim();
};

const declaredLeadTimeDisplay = (link: SupplierSkuLinkView): string => {
  if (link.leadTimeMeanDays === null) return '—';
  return link.leadTimeStdDays !== null
    ? `${link.leadTimeMeanDays} ± ${link.leadTimeStdDays} days`
    : `${link.leadTimeMeanDays} days`;
};

function SuppliedSkusPanel({
  suppliedSkus,
}: {
  suppliedSkus: SupplierSkuLinkView[];
}) {
  if (suppliedSkus.length === 0) {
    return <p className='text-sm text-gray-500'>No SKUs linked yet.</p>;
  }

  const representative =
    suppliedSkus.find((link) => link.isPreferred) ?? suppliedSkus[0];
  const headers = [
    'SKU code',
    'Product',
    'Lead time',
    'MOQ',
    'Order multiple',
    'Incoterms',
  ];

  return (
    <div className='space-y-3'>
      <p className='text-sm font-semibold text-gray-900'>
        Supplied SKUs and terms
      </p>
      <div className='overflow-hidden rounded-lg border border-gray-200 bg-white'>
        <table className='w-full'>
          <thead className='bg-gray-50'>
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='px-4 py-2 text-left text-xs font-medium text-gray-500'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {suppliedSkus.map((link, index) => (
              <tr key={link.inventorySkuId ?? `${link.skuCode}-${index}`}>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {link.skuCode || '—'}
                  {link.isPreferred && (
                    <span className='ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700'>
                      Preferred
                    </span>
                  )}
                </td>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {link.productName || '—'}
                </td>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {leadTimeDisplay(link)}
                </td>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {link.minimumOrderQuantity ?? '—'}
                </td>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {orderMultipleDisplay(link)}
                </td>
                <td className='px-4 py-2 text-sm text-gray-700'>
                  {link.incoterms || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500'>
        <span>Declared lead time {declaredLeadTimeDisplay(representative)}</span>
        <span aria-hidden>·</span>
        <span>Observed (GRN, last 90 days) — Not enough data</span>
        <span aria-hidden>·</span>
        <span>Incoterms {representative.incoterms || '—'}</span>
      </div>
    </div>
  );
}

function ReadinessCell({ readiness }: { readiness: SupplierReadiness | null }) {
  if (!readiness) return <span className='text-sm text-gray-400'>—</span>;
  if (readiness.ready) {
    return (
      <Badge
        intent='success'
        label='Source-ready'
        icon={<CheckIcon className='h-3.5 w-3.5' />}
      />
    );
  }
  return (
    <Badge
      intent='warning'
      label={`${readiness.gaps} gap${readiness.gaps === 1 ? '' : 's'}`}
    />
  );
}

export function SupplierTableRow({
  supplier,
  menuOptions,
  isCopied,
  onCopy,
  isExpanded,
  onToggleExpand,
}: SupplierTableRowProps) {
  const visibleBrands = supplier.brands.slice(0, MAX_BRAND_CHIPS);
  const overflowBrands = supplier.brands.length - visibleBrands.length;

  return (
    <>
      <tr className='align-middle hover:bg-gray-50'>
        <td className='w-10 px-2'>
          <button
            type='button'
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} supplied SKUs for ${
              supplier.name
            }`}
            className='rounded p-1 transition-colors hover:bg-gray-200'
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(supplier.id);
            }}
          >
            <ChevronRightIcon
              className={`h-4 w-4 text-gray-500 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </button>
        </td>
        <td className='px-4 py-4'>
          <p className='truncate text-sm font-semibold text-gray-900'>
            {supplier.name}
          </p>
          <p className='truncate text-xs text-gray-500'>
            {pluralise(supplier.locations.length, 'site')} ·{' '}
            {pluralise(supplier.suppliedSkus.length, 'linked SKU')}
          </p>
        </td>
      <td className='px-4 py-4'>
        <div className='flex items-center gap-1.5 text-sm text-gray-600'>
          <span className='truncate'>{supplier.supplierId}</span>
          <button
            type='button'
            aria-label={`Copy supplier ID for ${supplier.name}`}
            className='shrink-0'
            onClick={(e) => {
              e.stopPropagation();
              onCopy(supplier);
            }}
          >
            {isCopied ? (
              <CheckIcon className='h-4 w-4 text-emerald-600' />
            ) : (
              <ClipboardDocumentIcon className='h-4 w-4 text-gray-400 hover:text-primary' />
            )}
          </button>
        </div>
      </td>
      <td className='px-4 py-4'>
        {supplier.sourceType ? (
          <Badge
            intent={sourceTypeBadgeIntentOf(supplier.sourceType)}
            label={sourceTypeLabelOf(supplier.sourceType)}
          />
        ) : (
          <span className='text-sm text-gray-400'>—</span>
        )}
      </td>
      <td className='px-4 py-4'>
        {visibleBrands.length ? (
          <div className='flex flex-wrap items-center gap-1'>
            {visibleBrands.map((brand) => (
              <span
                key={brand}
                className='inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700'
              >
                {brand}
              </span>
            ))}
            {overflowBrands > 0 && (
              <span className='text-xs text-gray-400'>+{overflowBrands}</span>
            )}
          </div>
        ) : (
          <span className='text-sm text-gray-400'>—</span>
        )}
      </td>
      <td className='px-4 py-4'>
        <Badge
          intent={certBadgeIntentOf(supplier.certStatus)}
          label={certLabelOf(supplier)}
        />
      </td>
      <td className='px-4 py-4'>
        <ReadinessCell readiness={supplier.readiness} />
      </td>
      <td className='px-4 py-4'>
        <Badge
          intent={statusBadgeIntentOf(supplier.status)}
          label={statusLabelOf(supplier.status)}
        />
      </td>
      <td className='px-4 py-4'>
        <div className='flex items-center justify-end'>
          <SkuActionsMenu id={supplier.supplierId} options={menuOptions} />
        </div>
      </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={9} className='bg-gray-50 px-6 py-4'>
            <SuppliedSkusPanel suppliedSkus={supplier.suppliedSkus} />
          </td>
        </tr>
      )}
    </>
  );
}
